import React, { useState } from 'react';

const AdminChart = ({ monthlyData = [], isLoading = false }) => {
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [showBreakdown, setShowBreakdown] = useState(false);

    const maxValue = Math.max(...monthlyData.map(data => data.totalAppointments), 1);

    const handleBarClick = (monthData) => {
        setSelectedMonth(monthData);
        setShowBreakdown(true);
    };

    const closeBreakdown = () => {
        setShowBreakdown(false);
        setSelectedMonth(null);
    };

    if (isLoading) {
        return (
            <div className="text-center p-4">
                <div className="loading-spinner-container">
                    <div className="loading-spinner"></div>
                    <p>Loading chart data...</p>
                </div>
            </div>
        );
    }

    if (!monthlyData.length) {
        return (
            <div className="text-center p-4">
                <div className="py-3">
                    <i className="fa fa-bar-chart fa-3x text-muted mb-3"></i>
                    <h5>No Data Available</h5>
                    <p className="text-muted">Add physicians to see yearly schedule insights.</p>
                </div>
            </div>
        );
    }

    // Generate full calendar year from Jan to Dec
    const generateFullTimeline = () => {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const fullTimeline = [];

        // Generate January to December for current year
        for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
            const monthName = monthNames[monthIndex];
            const monthKey = `${monthName} ${currentYear}`;

            // Find existing data for this month
            const existingData = monthlyData.find(data => data.monthKey === monthKey);

            if (existingData) {
                fullTimeline.push(existingData);
            } else {
                // Create placeholder for empty month
                fullTimeline.push({
                    month: monthName,
                    year: currentYear,
                    monthKey,
                    totalAppointments: 0,
                    breakdown: [],
                    isEmpty: true
                });
            }
        }

        return fullTimeline;
    };

    const fullTimelineData = generateFullTimeline();

    return (
        <>
            <div className="admin-chart-container">
                <div className="chart-bars">
                    {fullTimelineData.map((monthData, index) => {
                        const barHeight = monthData.totalAppointments > 0 
                            ? (monthData.totalAppointments / maxValue) * 200 
                            : 8; // Minimum height for empty months
                        
                        const isEmpty = monthData.totalAppointments === 0;
                        
                        return (
                            <div 
                                key={monthData.monthKey} 
                                className="chart-bar-container"
                                onClick={() => !isEmpty && handleBarClick(monthData)}
                                style={{ cursor: isEmpty ? 'default' : 'pointer' }}
                                title={isEmpty ? 'No appointments this month' : `${monthData.totalAppointments} appointments - Click to view details`}
                            >
                                <div className="chart-bar-wrapper">
                                    <div 
                                        className={`chart-bar ${isEmpty ? 'empty-bar' : ''}`}
                                        style={{ 
                                            height: `${barHeight}px`,
                                            backgroundColor: isEmpty ? '#e9ecef' : '#E07A55',
                                            transition: 'all 0.3s ease',
                                            border: isEmpty ? '2px dashed #dee2e6' : 'none'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isEmpty) {
                                                e.target.style.backgroundColor = '#c86a45';
                                                e.target.style.transform = 'scale(1.05)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isEmpty) {
                                                e.target.style.backgroundColor = '#E07A55';
                                                e.target.style.transform = 'scale(1)';
                                            }
                                        }}
                                    />
                                    <div className={`chart-value ${isEmpty ? 'empty-value' : ''}`}>
                                        {isEmpty ? '0' : monthData.totalAppointments}
                                    </div>
                                </div>
                                <div className="chart-label">
                                    <div className="month">{monthData.month}</div>
                                    <div className="year">{monthData.year}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Breakdown Modal */}
            {showBreakdown && selectedMonth && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Schedule Breakdown - {selectedMonth.monthKey}
                                    <span className="badge bg-primary ms-2">{selectedMonth.totalAppointments} Total</span>
                                </h5>
                                <button type="button" className="btn-close" onClick={closeBreakdown}></button>
                            </div>
                            <div className="modal-body">
                                {selectedMonth.breakdown.map((physicianData) => (
                                    <div key={physicianData.physicianId} className="mb-4 border-bottom pb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <div>
                                                <h6 className="mb-1">
                                                    <i className="fa fa-user-md text-primary me-2"></i>
                                                    {physicianData.physicianName}
                                                </h6>
                                                <small className="text-muted">
                                                    {physicianData.specialty} • {physicianData.hospital}
                                                </small>
                                            </div>
                                            <span className="badge bg-secondary">
                                                {physicianData.appointmentCount} appointments
                                            </span>
                                        </div>
                                        
                                        <div className="row">
                                            {physicianData.appointments.map((appointment, index) => (
                                                <div key={appointment.id} className="col-md-6 mb-2">
                                                    <div className="card card-sm">
                                                        <div className="card-body p-2">
                                                            <div className="d-flex justify-content-between align-items-start">
                                                                <div className="flex-grow-1">
                                                                    <div className="d-flex align-items-center mb-1">
                                                                        <i className="fa fa-calendar text-muted me-1" style={{fontSize: '12px'}}></i>
                                                                        <small className="fw-bold">
                                                                            {new Date(appointment.date).toLocaleDateString('en-US', { 
                                                                                month: 'short', 
                                                                                day: 'numeric' 
                                                                            })}
                                                                        </small>
                                                                        <span className="ms-2 text-muted" style={{fontSize: '12px'}}>
                                                                            {appointment.startTime} - {appointment.endTime}
                                                                        </span>
                                                                        {appointment.duration && (
                                                                            <span className="ms-2 text-muted" style={{fontSize: '11px'}}>
                                                                                ({appointment.duration}min)
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="mb-1">
                                                                        <small className="text-primary fw-medium">
                                                                            {appointment.procedure}
                                                                        </small>
                                                                        {appointment.status && (
                                                                            <span className={`badge ms-2 ${appointment.status === 'booked' ? 'bg-success' : 'bg-secondary'}`} style={{fontSize: '9px'}}>
                                                                                {appointment.status}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="d-flex align-items-center mb-1">
                                                                        <i className="fa fa-building text-muted me-1" style={{fontSize: '10px'}}></i>
                                                                        <small className="text-muted" style={{fontSize: '11px'}}>
                                                                            {appointment.hospital}
                                                                        </small>
                                                                    </div>
                                                                    {appointment.priority && appointment.priority !== 'routine' && (
                                                                        <div className="d-flex align-items-center">
                                                                            <i className="fa fa-flag text-warning me-1" style={{fontSize: '10px'}}></i>
                                                                            <small className="text-warning" style={{fontSize: '10px'}}>
                                                                                {appointment.priority.charAt(0).toUpperCase() + appointment.priority.slice(1)} Priority
                                                                            </small>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeBreakdown}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .admin-chart-container {
                    padding: 20px;
                    background: #f8f9fa;
                    border-radius: 8px;
                    margin-bottom: 20px;
                }

                .chart-bars {
                    display: flex;
                    align-items: flex-end;
                    justify-content: space-between;
                    gap: 8px;
                    min-height: 250px;
                    padding: 20px 0;
                    border-bottom: 2px solid #dee2e6;
                    position: relative;
                }

                .chart-bar-container {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    transition: all 0.2s ease;
                }

                .chart-bar-container:hover {
                    transform: translateY(-2px);
                }

                .chart-bar-wrapper {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    margin-bottom: 10px;
                }

                .chart-bar {
                    width: 24px;
                    min-height: 10px;
                    border-radius: 4px 4px 0 0;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    margin-bottom: 5px;
                }

                .chart-bar.empty-bar {
                    box-shadow: none;
                    border-radius: 2px;
                }

                .chart-value {
                    font-size: 12px;
                    font-weight: bold;
                    color: #333;
                    background: white;
                    padding: 2px 6px;
                    border-radius: 4px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                }

                .chart-value.empty-value {
                    color: #999;
                    background: #f8f9fa;
                    font-weight: normal;
                }

                .chart-label {
                    text-align: center;
                    font-size: 11px;
                    color: #666;
                }

                .chart-label .month {
                    font-weight: bold;
                    margin-bottom: 2px;
                }

                .chart-label .year {
                    font-size: 10px;
                    color: #999;
                }

                .card-sm .card-body {
                    padding: 8px 12px;
                }

                @media (max-width: 768px) {
                    .chart-bar {
                        width: 18px;
                    }
                    
                    .chart-label {
                        font-size: 10px;
                    }
                    
                    .chart-value {
                        font-size: 11px;
                        padding: 1px 4px;
                    }
                }
            `}</style>
        </>
    );
};

export default AdminChart; 